/**
 * Tagging contracts shared by the API, the workers, the agent tools and the
 * web app. Pure data — no zod / openapi imports — so it is safe as a runtime
 * export from the `@quovy/types` barrel.
 *
 * Plan: docs/exec-plans/active/tagging-email-threads-and-messages.md
 */

import { resourceTypeFromPublicId } from "./public-id";

export const TAG_ID_PREFIX = "tag_";

/** The catalog cap a single list_tags answer returns, including simulations. */
export const MAX_TAG_LIST_LIMIT = 200;

/** Bounds one tool call's work; the args come from an LLM steered by untrusted mail. */
export const MAX_TAGS_PER_CALL = 25;

/** A `tag_…` public id, as opposed to a catalog name a write path resolves. */
export function isTagPublicId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith(TAG_ID_PREFIX) &&
    value.length > TAG_ID_PREFIX.length
  );
}

/** Named palette keys; the UI maps each to `--tag-<name>-bg/fg` tokens. */
export const TAG_COLORS = [
  "gray",
  "red",
  "orange",
  "amber",
  "green",
  "teal",
  "blue",
  "indigo",
  "purple",
  "pink",
] as const;

export type TagColor = (typeof TAG_COLORS)[number];

export function isTagColor(value: string): value is TagColor {
  return (TAG_COLORS as readonly string[]).includes(value);
}

/** One registry drives validation, public-id resolution and UI labels. */
export const TAG_RESOURCE_DEFINITIONS = [
  { type: "email_thread", label: "email threads", title: "Email threads" },
  { type: "task", label: "tasks", title: "Tasks" },
] as const;

export type TagResourceType = (typeof TAG_RESOURCE_DEFINITIONS)[number]["type"];

type DefinitionTypes<T extends readonly { type: string }[]> = {
  [K in keyof T]: T[K] extends { type: infer ResourceType extends string } ? ResourceType : never;
};

export const TAG_RESOURCE_TYPES = TAG_RESOURCE_DEFINITIONS.map(
  (entry) => entry.type,
) as unknown as DefinitionTypes<typeof TAG_RESOURCE_DEFINITIONS>;

export function isTagResourceType(value: string): value is TagResourceType {
  return (TAG_RESOURCE_TYPES as readonly string[]).includes(value);
}

/**
 * The taggable resource type a public id belongs to, from its prefix, or
 * `null` when the prefix is unknown or the type is not taggable. Built on the
 * registry parser in `public-id.ts`, which splits on the first underscore so
 * five-character prefixes (`emsg_`) resolve too.
 */
export function tagResourceTypeFromPublicId(publicId: string): TagResourceType | null {
  const type = resourceTypeFromPublicId(publicId);
  return type && isTagResourceType(type) ? type : null;
}

/** Human label for the set of taggable types, for "cannot be tagged" messages. */
export const TAG_RESOURCE_TYPE_LABELS = Object.fromEntries(
  TAG_RESOURCE_DEFINITIONS.map((entry) => [entry.type, entry.label]),
) as Readonly<Record<TagResourceType, string>>;

/** Standalone control labels; {@link TAG_RESOURCE_TYPE_LABELS} is mid-sentence casing. */
export const TAG_RESOURCE_TYPE_TITLES = Object.fromEntries(
  TAG_RESOURCE_DEFINITIONS.map((entry) => [entry.type, entry.title]),
) as Readonly<Record<TagResourceType, string>>;

export function taggableTypesSentence(): string {
  return `Tagging supports ${TAG_RESOURCE_TYPES.map((t) => TAG_RESOURCE_TYPE_LABELS[t]).join(", ")}.`;
}

/**
 * A tag as embedded on the resource that carries it. `tag_resource_id` is
 * the `tgr_…` attachment row, so removal is one `DELETE /v1/tag-resources/:id`.
 */
export type TagSummary = {
  id: string;
  name: string;
  color: TagColor;
  tag_resource_id: string;
};
